import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal, Item, MaskLines } from "@/components/Reveal";
import DepthCard from "@/components/DepthCard";
import Magnetic from "@/components/Magnetic";
import { ArrowRight } from "lucide-react";

const examples = [
  { category: "Business system", title: "Custom ERP", body: "Operations, finance, inventory, projects and approvals shaped around how your company actually runs." },
  { category: "Business system", title: "Custom CRM", body: "A customer system built around your sales process, service model, data and permissions, without permanent per-seat rent to us." },
  { category: "Business system", title: "Internal and client products", body: "Portals, dashboards, mobile tools and workflow systems for teams, customers, partners and vendors." },
  { category: "AI system", title: "AI agents", body: "Agents that interpret work, reason with company rules, draft actions and operate across existing systems under human control." },
  { category: "Intelligence layer", title: "Company Brain", body: "Governed company knowledge that combines data, documents, policy, permissions, sources and evaluated answers." },
  { category: "AI-native system", title: "AI-native ERP and CRM", body: "Core business software where search, drafting, prediction and agents are built into the workflow instead of added beside it." },
  { category: "Operating layer", title: "Company OS", body: "One layer coordinating people, systems, agents, approvals, exceptions and a complete record of every decision." },
  { category: "Specialised AI", title: "Adapted open-weight models", body: "Private deployment, evaluation and targeted fine-tuning for narrow company tasks when retrieval alone is not enough." },
];

/* Derived from `examples` rather than written out again, so the machine-readable
   list cannot drift from the cards actually rendered below it. Attached to the
   same #organization @id used in index.html and /ai-profile, so this reads as
   the service list of the existing company, not a new one. */
const catalogSchema = {
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "What AUT labs builds",
  url: "https://autlabs.in/what-we-build",
  provider: { "@id": "https://autlabs.in/#organization" },
  itemListElement: examples.map((e, i) => ({
    "@type": "Offer",
    position: i + 1,
    itemOffered: {
      "@type": "Service",
      name: e.title,
      category: e.category,
      description: e.body,
      areaServed: { "@type": "Country", name: "India" },
      provider: { "@id": "https://autlabs.in/#organization" },
    },
  })),
};

export default function WhatWeBuild() {
  return (
    <div data-testid="page-what-we-build">
      <Seo
        title="What we build · AUT labs"
        description="Custom ERP and CRM systems, AI agents, Company Brains, AI-native business systems, Company OS platforms and specialised model deployments built by AUT labs."
        schema={catalogSchema}
      />

      {/* Intro */}
      <section className="border-b border-ink col-rules">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
          <Reveal className="grid grid-cols-1 md:grid-cols-12">
            <div className="md:col-span-8 lg:col-span-7">
              <Item as="p" className="eyebrow mb-6">What we build</Item>
              <h1 className="display text-[40px] md:text-[72px] lg:text-[88px] leading-[0.96]">
                <MaskLines lines={["Nothing here comes", "off the shelf."]} />
              </h1>
              <Item as="p" className="mt-8 max-w-xl text-lg text-ink/80">
                Every tool starts with a specific business and a specific job. We reuse
                what is proven underneath, but the workflow, rules, and approvals are yours.
              </Item>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The capability architecture, from owned software to specialised AI. */}
      <section className="border-b border-ink" data-testid="examples-grid">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-14 md:py-20">
          <Reveal className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-ink">
            {examples.map((e, i) => (
              <Item key={i} className="border-r border-b border-ink">
                <DepthCard className="h-full">
                  <div className="p-6 md:p-9 min-h-[230px] h-full flex flex-col">
                    <span className="eyebrow text-ember-deep">{e.category}</span>
                    <div className="flex gap-5 items-start mt-auto pt-8">
                    <span className="numeral text-[32px] md:text-[44px] pt-1 shrink-0" data-testid={`example-num-${i}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                      <div>
                        <h2 className="display text-2xl md:text-[32px] leading-[1.08]" data-testid={`example-${i}`}>{e.title}</h2>
                        <p className="text-base md:text-lg text-ink/75 mt-3">{e.body}</p>
                      </div>
                    </div>
                  </div>
                </DepthCard>
              </Item>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Closing band */}
      <section data-testid="wwb-closing">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
          <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <Item className="md:col-span-8">
              <h2 className="display text-[34px] md:text-[64px] leading-[1]">
                Start with one useful improvement. Build further only when it makes sense.
              </h2>
            </Item>
            <Item className="md:col-span-4 md:text-right">
              <Magnetic>
                <Link to="/book" className="pill pill-cobalt" data-testid="wwb-book-btn">
                  Book a 15-minute call <ArrowRight size={16} />
                </Link>
              </Magnetic>
            </Item>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
